// CRM-ONBOARDING-TAB-2026-10-13: the Onboarding tab's rows. Pure: one onboarding_overview() row (one per link, see
// migrations/2026-10-13-onboarding-overview.sql) + the CRM's own candidate record -> what the list shows. The "next step
// for staff" is derived here and never typed anywhere.
import { daysLeft, isRed, NO_MIDDLE_NAME_TEXT } from './onboarding';

const DAY = 864e5;
export const EXPIRING_DAYS = 2;   // "Link expiring (≤2 days)"
export const STALLED_DAYS = 3;    // "Stalled (no activity in 3+ days)"
// Hub ruling 2026-10-13: an expired link (not revoked, not complete) needs staff -- extend it or resend -- for 14 days
// after its expiry; after that it is inactive (Expired/Revoked).
export const EXPIRED_GRACE_DAYS = 14;

export const STEPS = {
  waiting: 'Waiting on candidate',
  expiring: 'Link expiring',
  stalled: 'Stalled',
  packet: 'Ready: download packet',
  sterling: 'Send to Sterling',
  adp: 'Enter in ADP',
  complete: 'Complete',
  extend: 'Link expired: extend or resend',   // expired up to 14 days ago: Needs me
  expired: 'Link expired',   // expired more than 14 days ago -- inactive: the Expired/Revoked filter, never "Needs me"
  revoked: 'Revoked',        // inactive
  // ONBOARD-EXAMS-v2-2026-10-16: sent exams but no onboarding link -- listed (All active / All), never "Needs me"
  exams: 'Exams only',
};
const INACTIVE = new Set(['expired', 'revoked']);
const t = (iso) => (iso ? new Date(iso).getTime() : NaN);

/** 'open' | 'expired' | 'revoked' | 'complete' -- a pending / in-progress link past its expiry is expired. */
export function linkState(row, now = Date.now()) {
  if (row.status === 'revoked') return 'revoked';
  if (row.status === 'complete') return 'complete';
  if (row.status === 'expired' || t(row.expires_at) < now) return 'expired';
  return 'open';
}

/** Days until the link expires, rounded up (0 = expires within the day); negative once it has. */
export function linkDaysLeft(row, now = Date.now()) {
  const ms = t(row.expires_at) - now;
  return Number.isNaN(ms) ? null : Math.ceil(ms / DAY);
}

/** The next step for staff -- one of STEPS' keys. */
export function nextStep(row, now = Date.now()) {
  if (row.entered_in_adp_at) return 'complete';
  const state = linkState(row, now);
  if (state === 'revoked') return 'revoked';
  if (state === 'complete') {
    if (!row.packet_downloaded_at) return 'packet';
    if (row.background_signed_at && !row.sent_to_sterling_at) return 'sterling';
    return 'adp';
  }
  if (state === 'expired') return now - t(row.expires_at) <= EXPIRED_GRACE_DAYS * DAY ? 'extend' : 'expired';
  if (t(row.expires_at) - now <= EXPIRING_DAYS * DAY) return 'expiring';
  if (now - t(row.last_activity_at || row.created_at) >= STALLED_DAYS * DAY) return 'stalled';
  return 'waiting';
}

/** The flags column: [{ key, text }]. */
export function rowFlags(row, now = Date.now()) {
  const out = [];
  if (row.background_signed_at && !row.degree_saved_at) out.push({ key: 'degree', text: 'Degree details missing' });
  if (row.no_middle_name === true) out.push({ key: 'middle', text: NO_MIDDLE_NAME_TEXT });   // the same words as the background row
  if (linkState(row, now) === 'expired' && !row.entered_in_adp_at) out.push({ key: 'expired', text: 'Link expired' });
  return out;
}

export const isTestCandidate = (c) => Array.isArray(c && c.tags) && c.tags.some((x) => String(x).trim().toLowerCase() === 'test');

/** overview rows + the CRM's candidates -> display rows (newest link first, as the RPC orders them). */
export function shapeOverview(rows, candidates, now = Date.now()) {
  const byId = new Map((candidates || []).map((c) => [c.id, c]));
  return (rows || []).map((r) => {
    const c = byId.get(r.candidate_id);
    const step = nextStep(r, now);
    return {
      ...r,
      name: (c && c.name) || `Candidate #${r.candidate_id}`,
      discipline: (c && c.specialty) || '',
      test: isTestCandidate(c),
      step,
      stepText: STEPS[step],
      daysLeft: linkDaysLeft(r, now),
      flags: rowFlags(r, now),
    };
  });
}

// The filters, in the order they show. "Needs me" = everything not "Waiting on candidate" or "Complete" among the active
// rows; a test candidate never counts there (nor in the nav badge).
export const FILTERS = [
  ['active', 'All active'],
  ['needs', 'Needs me'],
  ['progress', 'In progress'],
  ['completed', 'Completed'],
  ['inactive', 'Expired/Revoked'],
  ['all', 'All'],
];
const isActive = (r) => !INACTIVE.has(r.step) && r.step !== 'complete';
const MATCH = {
  active: isActive,
  needs: (r) => isActive(r) && r.step !== 'waiting' && r.step !== 'exams' && !r.test,
  progress: (r) => isActive(r) && r.status === 'in_progress' && ['waiting', 'expiring', 'stalled'].includes(r.step),
  completed: (r) => r.step === 'complete',
  inactive: (r) => INACTIVE.has(r.step),
  all: () => true,
};
export const DEFAULT_FILTER = 'active';

export function filterRows(rows, filter = DEFAULT_FILTER, search = '') {
  const words = String(search || '').toLowerCase().split(/\s+/).filter(Boolean);
  return rows.filter((r) => (MATCH[filter] || MATCH.active)(r)
    && words.every((w) => String(r.name).toLowerCase().includes(w)));
}

export function filterCounts(rows) {
  return Object.fromEntries(FILTERS.map(([k]) => [k, rows.filter(MATCH[k]).length]));
}

/** The header's "Expiring soon (N)": the Expirations rows Expirations itself shows in red. */
export function expiringSoonCount(expirationRows, now = new Date()) {
  return (expirationRows || []).filter((r) => isRed(daysLeft(r.expires_on, now))).length;
}

/**
 * ONBOARD-EXAMS-v2-2026-10-16: a row for each candidate who was sent exams but has no onboarding link (the overview's
 * rows are links). examLinks: onboarding_exam_links rows. Newest exam link first.
 */
export function examOnlyRows(rows, examLinks, candidates) {
  const hasLink = new Set((rows || []).map((r) => r.candidate_id));
  const byId = new Map((candidates || []).map((c) => [c.id, c]));
  const first = new Map();
  for (const l of examLinks || []) {
    if (hasLink.has(l.candidate_id)) continue;
    const seen = first.get(l.candidate_id);
    if (!seen || new Date(l.created_at) > new Date(seen.created_at)) first.set(l.candidate_id, l);
  }
  return [...first.values()].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).map((l) => {
    const c = byId.get(l.candidate_id);
    return { session_id: `exam-${l.candidate_id}`, candidate_id: l.candidate_id, status: 'exams', created_at: l.created_at,
      name: (c && c.name) || `Candidate #${l.candidate_id}`, discipline: (c && c.specialty) || '', test: isTestCandidate(c),
      step: 'exams', stepText: STEPS.exams, daysLeft: null, flags: [] };
  });
}
